import React, { useState, useEffect } from 'react';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { api, API_BASE_URL, getHeaders } from '../config/api';

const MeusDados: React.FC = () => {
  const [meusDados, setMeusDados] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isExclusaoModalOpen, setIsExclusaoModalOpen] = useState(false);
  const [motivoExclusao, setMotivoExclusao] = useState('');
  const [enviandoExclusao, setEnviandoExclusao] = useState(false);
  const [isRevogacaoModalOpen, setIsRevogacaoModalOpen] = useState(false);

  useEffect(() => {
    carregarMeusDados();
  }, []);

  const carregarMeusDados = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dados-pessoais/meus-dados');
      setMeusDados(response.data?.data || response.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const solicitarExclusao = async () => {
    if (!motivoExclusao.trim()) {
      alert('Por favor, informe o motivo da solicitação de exclusão.');
      return;
    }

    try {
      setEnviandoExclusao(true);
      await api.post('/dados-pessoais/solicitar-exclusao', { motivo: motivoExclusao });
      alert('Solicitação de exclusão enviada com sucesso. O DPO analisará sua solicitação.');
      setIsExclusaoModalOpen(false);
      setMotivoExclusao('');
    } catch (error: any) {
      console.error('Erro ao solicitar exclusão:', error);
      alert(`Erro: ${error.response?.data?.error || 'Erro ao enviar solicitação'}`);
    } finally {
      setEnviandoExclusao(false);
    }
  };

  const revogarConsentimento = async () => {
    setIsRevogacaoModalOpen(true);
  };

  const exportarDados = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dados-pessoais/exportar`, {
        headers: getHeaders(true)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dados_pessoais_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const error = await response.json().catch(() => ({}));
        alert(`Erro: ${error.error || 'Erro ao exportar dados'}`);
      }
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      alert('Erro ao exportar dados. Tente novamente.');
    }
  };

  const confirmarRevogacao = async () => {
    try {
      await api.put('/dados-pessoais/revogar-consentimento', {
        tipo_consentimento: 'politica_privacidade',
        motivo: 'Revogação pelo titular'
      });
      alert('Consentimento revogado com sucesso.');
      carregarMeusDados();
    } catch (error: any) {
      console.error('Erro ao revogar consentimento:', error);
      alert(`Erro: ${error.response?.data?.error || 'Erro ao revogar consentimento'}`);
    } finally {
      setIsRevogacaoModalOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Meus Dados Pessoais</h1>
        <p className="text-gray-600 mb-6">
          Conforme a LGPD, você tem direito de acessar, corrigir e solicitar a exclusão dos seus dados pessoais.
        </p>

        {/* Dados do Usuário */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Dados de Cadastro</h2>
          {meusDados?.usuario && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nome</label>
                <p className="mt-1 text-sm text-gray-900">{meusDados.usuario.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">E-mail</label>
                <p className="mt-1 text-sm text-gray-900">{meusDados.usuario.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Login</label>
                <p className="mt-1 text-sm text-gray-900">{meusDados.usuario.login}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Telefone</label>
                <p className="mt-1 text-sm text-gray-900">{meusDados.usuario.telefone || 'Não informado'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">CNPJ</label>
                <p className="mt-1 text-sm text-gray-900">{meusDados.usuario.cnpj || 'Não informado'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Data de Cadastro</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(meusDados.usuario.criado_em).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Consentimentos */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Consentimentos</h2>
          {meusDados?.consentimentos && meusDados.consentimentos.length > 0 ? (
            <div className="space-y-3">
              {meusDados.consentimentos.map((consentimento: any) => (
                <div key={consentimento.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {consentimento.tipo_consentimento === 'politica_privacidade' ? 'Política de Privacidade' : consentimento.tipo_consentimento}
                      </p>
                      <p className="text-sm text-gray-600">
                        Aceito em: {new Date(consentimento.data_consentimento).toLocaleDateString('pt-BR')}
                      </p>
                      <p className="text-sm text-gray-600">
                        Versão: {consentimento.versao_politica}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      consentimento.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {consentimento.ativo ? 'Ativo' : 'Revogado'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">Nenhum consentimento registrado.</p>
          )}
        </div>

        {/* Ações */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Ações Disponíveis</h2>
          <div className="space-y-4">
            <Button
              onClick={() => setIsExclusaoModalOpen(true)}
              className="bg-red-600 hover:bg-red-700"
            >
              Solicitar Exclusão de Dados
            </Button>
            <Button
              onClick={exportarDados}
              variant="secondary"
              className="ml-4"
            >
              Exportar Dados (Portabilidade)
            </Button>
            {meusDados?.consentimentos?.some((c: any) => c.tipo_consentimento === 'politica_privacidade' && c.ativo) && (
              <Button
                onClick={revogarConsentimento}
                variant="secondary"
                className="ml-4"
              >
                Revogar Consentimento
              </Button>
            )}
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Ao solicitar a exclusão dos seus dados, o DPO analisará sua solicitação e entrará em contato.
          </p>
        </div>
      </div>

      {/* Modal de Exclusão */}
      <Modal isOpen={isExclusaoModalOpen} onClose={() => setIsExclusaoModalOpen(false)} title="Solicitar Exclusão de Dados">
        <div className="space-y-4">
          <p className="text-gray-700">
            Esta ação iniciará o processo de exclusão dos seus dados pessoais conforme a LGPD.
            O DPO analisará sua solicitação e entrará em contato.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo da solicitação *
            </label>
            <textarea
              value={motivoExclusao}
              onChange={(e) => setMotivoExclusao(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
              placeholder="Descreva o motivo pelo qual deseja a exclusão dos seus dados..."
            />
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={solicitarExclusao}
              className="flex-1 bg-red-600 hover:bg-red-700"
              loading={enviandoExclusao}
            >
              Confirmar Solicitação
            </Button>
            <Button
              onClick={() => setIsExclusaoModalOpen(false)}
              variant="secondary"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Revogação */}
      <Modal isOpen={isRevogacaoModalOpen} onClose={() => setIsRevogacaoModalOpen(false)} title="Revogar Consentimento">
        <div className="space-y-4">
          <p className="text-gray-700">
            Tem certeza que deseja revogar o consentimento da Política de Privacidade?
            Isso pode afetar o uso do sistema.
          </p>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={confirmarRevogacao}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Confirmar Revogação
            </Button>
            <Button
              onClick={() => setIsRevogacaoModalOpen(false)}
              variant="secondary"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MeusDados;
